import React from 'react';
import { CarouselRoot } from './CarouselRoot';
import { CarouselTrack } from './CarouselTrack';
import { CarouselItem } from './CarouselItem';
import { carouselFadeEdge, carouselTrackFadePadding, combineCarouselClasses } from './carousel.styles';
import type { CarouselProps } from './types';

/**
 * Carousel - Simple API for horizontal scrolling carousel
 *
 * This is the main carousel component that provides a simple, easy-to-use API.
 * It automatically wraps children in CarouselItem components and handles the basic layout.
 *
 * For more advanced usage with custom headers, controls, etc., use the compound API:
 * Carousel.Root, Carousel.Track, Carousel.Item, etc.
 *
 * @example
 * ```tsx
 * <Carousel gap="md" snapAlign="start" aria-label="Featured properties">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </Carousel>
 * ```
 */
export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      gap = 'md',
      snapAlign = 'start',
      scrollBehavior = 'smooth',
      enableSnap = true,
      fadeEdge = false,
      children,
      className,
      'aria-label': ariaLabel,
      ...restProps
    },
    ref,
  ) => {
    // Convert children to CarouselItem components if they aren't already
    const items = React.Children.map(children, (child, index) => {
      // If child is already a CarouselItem, return as-is
      if (React.isValidElement(child) && child.type === CarouselItem) {
        return child;
      }

      // Otherwise, wrap in CarouselItem
      return <CarouselItem key={index}>{child}</CarouselItem>;
    });

    const rootClassName = combineCarouselClasses(className, fadeEdge && carouselFadeEdge);

    return (
      <CarouselRoot ref={ref} className={rootClassName} {...restProps}>
        <CarouselTrack
          gap={gap}
          snapAlign={snapAlign}
          scrollBehavior={scrollBehavior}
          enableSnap={enableSnap}
          className={fadeEdge ? carouselTrackFadePadding : undefined}
          aria-label={ariaLabel}
        >
          {items}
        </CarouselTrack>
      </CarouselRoot>
    );
  },
);

Carousel.displayName = 'Carousel';
